import { useState } from "react";

export interface Facility {
  id: string;
  name: string;
  type: "USPS" | "Clerk" | "Library" | "Municipal";
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  hours: string;
  appointmentStatus: "appointment-required" | "call-to-confirm" | "walk-in-ok";
  walkIn: boolean;
  distanceMiles: number;
  notes: string;
}

interface FacilityAccordionProps {
  cityName: string;
  stateName: string;
  facilities: Facility[];
}

const typeLabel: Record<Facility["type"], string> = {
  USPS: "USPS Post Office",
  Clerk: "County Clerk",
  Library: "Public Library",
  Municipal: "City Office",
};

const statusBadge: Record<Facility["appointmentStatus"], { text: string; className: string }> = {
  "appointment-required": { text: "Appointment Required", className: "bg-red-50 text-red-700 border-red-200" },
  "call-to-confirm": { text: "Call to Confirm", className: "bg-amber-50 text-amber-700 border-amber-200" },
  "walk-in-ok": { text: "Walk-Ins Welcome", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function FacilityAccordion({ cityName, stateName, facilities }: FacilityAccordionProps) {
  const [openId, setOpenId] = useState<string | null>(facilities[0]?.id ?? null);

  const toggle = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  return (
    <section className="w-full bg-white py-16 md:py-20">
      <div className="mx-auto max-w-5xl px-4 md:px-6">
        <div className="mb-10 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-900">
            Passport Acceptance Agents Near {cityName}, {stateName}
          </h2>
          <p className="mt-3 text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
            These official passport acceptance facilities are located in and around {cityName}. 
            Call ahead to confirm hours, appointments, and photo service availability.
          </p>
        </div>

        <div className="space-y-3">
          {facilities.map((facility) => {
            const isOpen = openId === facility.id;
            const status = statusBadge[facility.appointmentStatus];

            return (
              <div
                key={facility.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggle(facility.id)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                  aria-expanded={isOpen}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base md:text-lg font-semibold text-gray-900">
                        {facility.name}
                      </h3>
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 whitespace-nowrap">
                        {typeLabel[facility.type]}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600 truncate">
                      {facility.address}, {facility.city}, {facility.state} {facility.zip}
                    </p>
                  </div>
                  <div className="ml-3 flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-500 hidden sm:inline">
                      {facility.distanceMiles.toFixed(1)} mi
                    </span>
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i
                        className={`ri-arrow-down-s-line text-lg text-gray-500 transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="p-4 md:p-5 bg-white border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Address
                        </p>
                        <p className="text-sm text-gray-900">
                          {facility.address}
                          <br />
                          {facility.city}, {facility.state} {facility.zip}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Phone
                        </p>
                        <a
                          href={`tel:${facility.phone.replace(/\D/g, "")}`}
                          className="text-sm text-gray-900 hover:text-emerald-700 transition-colors"
                        >
                          {facility.phone}
                        </a>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Hours
                        </p>
                        <p className="text-sm text-gray-900">{facility.hours}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Appointment
                        </p>
                        <span
                          className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full border ${status.className}`}
                        >
                          {status.text}
                        </span>
                      </div>
                    </div>

                    {facility.notes && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-md">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                          Important Notes
                        </p>
                        <p className="text-sm text-gray-700">{facility.notes}</p>
                      </div>
                    )}

                    <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line" />
                      </div>
                      <span>Approximately {facility.distanceMiles.toFixed(1)} miles from {cityName} center</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
              <i className="ri-information-line text-gray-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Before You Visit</p>
              <p className="text-sm text-gray-600 mt-1">
                Always call the facility ahead of time to confirm they are currently accepting passport applications, 
                verify their hours, and ask if they offer photo services. Some locations have limited hours or 
                require appointments that fill up quickly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}